import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, KeyRound, Users, LifeBuoy, Lock, Wand2,
  Check, ArrowRight, Smartphone, Sparkles, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import InstallButton from "@/components/InstallButton";
import ThemeToggle from "@/components/ThemeToggle";
import { BrandHomeLink, BrandShield } from "@/components/BrandLogo";

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen" style={{ background: "var(--vault-app-bg)" }}>
      {/* Nav */}
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandHomeLink size="md" testid="landing-brand-logo" linkTestid="brand-link" />
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <a href="#features" className="text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]">Features</a>
            <a href="#security" className="text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]">Security</a>
            <a href="#faq" className="text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]">FAQ</a>
            <Link to="/contact" className="text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]" data-testid="nav-contact">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle testid="landing-theme-toggle" />
            <InstallButton variant="outline" className="hidden sm:inline-flex" />
            {user && user !== false ? (
              <Button asChild className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="nav-open-vault">
                <Link to="/vault">Open vault <ArrowRight size={16} className="ml-1.5" /></Link>
              </Button>
            ) : (
              <Button asChild variant="ghost" className="rounded-xl" data-testid="nav-signin">
                <Link to="/login">Sign in</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero logo */}
      <motion.div
        initial={{ opacity: 0, y: -12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 flex justify-center"
      >
        <img
          src="/ezguard-logo-full.webp"
          alt="EZGuard"
          className="hero-logo w-56 sm:w-72 lg:w-80 h-auto select-none"
          draggable={false}
          data-testid="hero-logo"
        />
      </motion.div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-8 pb-20 lg:pt-12 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 pill mb-5"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={12} /> A password vault built for couples & families
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight"
          >
            The keys to your life,
            <br />
            <span className="text-brand-gradient" data-testid="hero-gradient-text">kept safe for the people who matter.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.12 }}
            className="text-[color:var(--vault-text-secondary)] mt-5 text-base sm:text-lg max-w-xl leading-relaxed"
          >
            Store all your passwords behind a single master password. Share specific accounts with your spouse.
            And designate trusted contacts who can reach your vault if something happens to you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <Button
              asChild
              className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white px-6 py-6 text-base"
              data-testid="hero-get-started-btn"
            >
              <Link to="/login">
                Sign in to your vault
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </Button>
            <a href="#features" className="text-sm text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] inline-flex items-center gap-1">
              See what's inside
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.3 }}
            className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[color:var(--vault-text-tertiary)]"
            data-testid="hero-trust-row"
          >
            <span className="inline-flex items-center gap-1.5"><Lock size={14} /> AES-256 encrypted</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14} /> Invite-only for your family</span>
            <span className="inline-flex items-center gap-1.5"><Smartphone size={14} /> Installable on any device</span>
          </motion.div>
        </div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="vault-card p-6 grain-overlay">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--vault-gradient)" }}>
                <KeyRound size={18} />
              </div>
              <div>
                <div className="font-[Outfit] font-medium">Family bank</div>
                <div className="text-xs text-[color:var(--vault-text-tertiary)]">sarah@example.com</div>
              </div>
              <span className="pill ml-auto">Banking</span>
            </div>
            <div className="rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] px-3 py-2 font-mono tracking-wider text-sm">
              ••••••••••••••
            </div>
            <div className="mt-3 flex gap-1.5">
              {[1,2,3,4].map((i) => (
                <div key={i} className="strength-seg" style={{ background: i <= 4 ? "var(--vault-success)" : "" }} />
              ))}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {[
                { label: "Vault items", value: "42" },
                { label: "Shared", value: "7" },
                { label: "Contacts", value: "2" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] p-3">
                  <div className="font-[Outfit] text-xl font-semibold">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--vault-text-tertiary)]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white border-y border-[color:var(--vault-border)]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.1em] text-[color:var(--vault-text-tertiary)]">What's inside</p>
            <h2 className="font-[Outfit] text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">
              Everything a family needs. Nothing they don't.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {[
              { icon: KeyRound, title: "Encrypted vault", body: "Add unlimited logins, secure notes, and cards. Passwords are encrypted with AES-256 at rest." },
              { icon: Wand2, title: "Strong password generator", body: "Generate cryptographically random passwords with a live strength meter — 8 to 48 characters." },
              { icon: Users, title: "Share with your spouse", body: "Pick exactly which items to share — Wi-Fi, bills, the family bank — with read or edit access." },
              { icon: LifeBuoy, title: "Emergency access", body: "Designate trusted contacts. If something happens, they can request access — auto-granted after your chosen wait period." },
              { icon: Smartphone, title: "Installable web app", body: "Install EZGuard to your phone or desktop home screen. Works offline-friendly with native-app feel." },
              { icon: RefreshCw, title: "Updates included", body: "Every new update we ship lands in your vault automatically. No upgrade fees, ever." },
            ].map((f) => (
              <div key={f.title} className="vault-card p-6 vault-card-hover" data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--vault-gradient)" }}>
                  <f.icon size={20} />
                </div>
                <h3 className="font-[Outfit] text-xl font-medium mt-4">{f.title}</h3>
                <p className="text-sm text-[color:var(--vault-text-secondary)] mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section id="security" className="max-w-6xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[color:var(--vault-text-tertiary)]">Designed around your security</p>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">
            One master password. Everything else, locked.
          </h2>
          <p className="text-[color:var(--vault-text-secondary)] mt-4 leading-relaxed">
            Your master password is hashed with bcrypt — we never see it. Vault passwords and secure notes are
            encrypted with AES-256-GCM before they're written to the database. Failed sign-in attempts trigger
            an automatic lockout to keep brute-force attackers out.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "bcrypt master-password hashing",
              "AES-256-GCM encryption at rest",
              "httpOnly cookies — tokens never touch JavaScript",
              "Rate-limiting & 15-minute lockout on failed sign-ins",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check size={16} className="text-[color:var(--vault-success)] mt-0.5 shrink-0" />
                <span className="text-[color:var(--vault-text-secondary)]">{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="vault-card p-8 grain-overlay">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: "var(--vault-gradient)" }}>
              <ShieldCheck size={22} />
            </div>
            <div>
              <div className="font-[Outfit] text-lg font-medium">Emergency access</div>
              <div className="text-xs text-[color:var(--vault-text-tertiary)]">3-day waiting period</div>
            </div>
          </div>
          <ol className="space-y-3 text-sm text-[color:var(--vault-text-secondary)]">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[color:var(--vault-brand-light)] text-[color:var(--vault-brand)] text-xs font-semibold flex items-center justify-center shrink-0">1</span><span>Designate one or more trusted contacts.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[color:var(--vault-brand-light)] text-[color:var(--vault-brand)] text-xs font-semibold flex items-center justify-center shrink-0">2</span><span>If something happens, they request access from their own account.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[color:var(--vault-brand-light)] text-[color:var(--vault-brand)] text-xs font-semibold flex items-center justify-center shrink-0">3</span><span>You can approve or deny instantly. Otherwise, access auto-grants after the waiting period.</span></li>
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 lg:px-8 py-20">
        <p className="text-xs uppercase tracking-[0.1em] text-[color:var(--vault-text-tertiary)] text-center">FAQ</p>
        <h2 className="font-[Outfit] text-3xl sm:text-4xl font-semibold mt-2 tracking-tight text-center">
          Questions, answered.
        </h2>
        <div className="mt-10 space-y-4">
          {[
            { q: "How do I get access?", a: "EZGuard is invite-only. The vault owner generates a one-time invite code and shares the link with you. Open it, set your name and master password, and you're in." },
            { q: "What happens if I forget my master password?", a: "Without it, the encrypted data in your vault cannot be recovered. That's the trade-off for true privacy. Designate a trusted contact via emergency access to keep things safe." },
            { q: "How is sharing different from emergency access?", a: "Sharing gives a specific person read or edit access to specific items. Emergency access gives them potential read access to your entire vault — but only after they request it and a waiting period passes." },
            { q: "Can I install it like an app?", a: "Yes. EZGuard is a Progressive Web App — add it to your home screen on iOS, Android, Windows, or macOS for a native-app feel." },
          ].map((item) => (
            <details key={item.q} className="vault-card p-5 group" data-testid={`faq-${item.q.slice(0, 16)}`}>
              <summary className="cursor-pointer font-[Outfit] text-lg font-medium flex items-center justify-between list-none">
                <span>{item.q}</span>
                <span className="text-[color:var(--vault-text-tertiary)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-[color:var(--vault-text-secondary)] mt-3 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[color:var(--vault-border)] bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[color:var(--vault-text-tertiary)]">
          <div className="flex items-center gap-2">
            <BrandShield className="w-7 h-7" />
            <span data-testid="landing-footer-copyright">© {new Date().getFullYear()} EZGuard</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/contact" className="hover:text-[color:var(--vault-brand)]">Contact</Link>
            <Link to="/login" className="hover:text-[color:var(--vault-brand)]">Sign in</Link>
            <Link to="/register" className="hover:text-[color:var(--vault-brand)]">Create account</Link>
          </div>
        </div>
        <div className="border-t border-[color:var(--vault-border)]">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 text-center text-xs text-[color:var(--vault-text-tertiary)]">
            Website Designed and Built by{" "}
            <a
              href="https://gadgetguymedia.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[color:var(--vault-brand)] hover:underline"
              data-testid="footer-credit-link"
            >
              Gadget Guy Media
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
